<template>
  <div class="recent-view">
    <div class="container-fluid">
      <section>
        <!-- Simple iframe with loading state -->
        <div v-if="isCheckingAccess" class="w-100 d-flex align-items-center justify-content-center" style="height: 70vh; background-color: #262653;">
          <div class="text-center">
            <div class="spinner-border text-primary mb-3" role="status">
              <span class="visually-hidden">Loading...</span>
            </div>
            <p class="text-white">{{ $t("Checking subscription...") }}</p>
          </div>
        </div>
        <div v-else-if="canPlay" class="game-wrapper">
          <iframe
            v-if="showGame"
            ref="gameIframe"
            :src="secureGameUrl"
            class="w-100 game-iframe"
            sandbox="allow-scripts allow-same-origin allow-popups allow-forms allow-modals allow-pointer-lock allow-orientation-lock"
            allow="autoplay; fullscreen; gamepad; microphone; camera; display-capture"
            referrerpolicy="no-referrer-when-downgrade"
            @load="onGameLoad"
            @error="onGameError"
          ></iframe>
          
          <div v-if="loading" class="loading-overlay">
            <div class="spinner-border text-primary" role="status">
              <span class="visually-hidden">Loading...</span>
            </div>
            <p>{{ $t("Loading game...") }}</p>
          </div>
        </div>

        <div
          v-else
          class="w-100 d-flex align-items-center justify-content-center"
          :style="{
            height: '70vh',
            backgroundColor: '#262653',
            backgroundRepeat: 'no-repeat',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundImage: `url(${game.thumb_1})`
          }"
        >
          <h1
            class="text-white text-center p-3 subscription-message"
            @click="openSubscription"
          >
            <span v-if="isLoggedIn">
              {{ $t("Your subscription finished, top-up balance and") }}
              {{ $t("activate subscription") }}.
            </span>
            <span v-else>
              {{ $t("Please login to play this game") }}, {{ $t("click") }}
              {{ $t("here") }} {{ $t("to login") }}.
            </span>
          </h1>
        </div>

        <div
          class="d-flex flex-column flex-md-row justify-content-between gap-4 my-4"
        >
          <!-- Title -->
          <div class="d-flex flex-column mx-2">
            <div class="d-flex justify-content-between">
              <div>
                <h4>{{ game.title }}</h4>
                <p class="small">
                  {{ $t("Played") }} {{ game.views ? game.views : 0 }}
                  {{ $t("times") }}.
                </p>
              </div>
              <div
                class="d-flex align-items-center justify-content-center rounded-circle bg-dark py-2 px-4 d-md-none"
                @click="toggleFullscreen"
              >
                <img src="@/assets/img/game/fullscreen.svg" alt="fullscreen" />
              </div>
            </div>
            <!-- Reviews -->
            <div class="d-flex align-items-center">
              <StarRating
                :rating="game.ratings_avg"
                starSize="18"
                :readonly="true"
              />
              <span class="small">
                {{ game.ratings_avg }} ({{ game.ratings_count }}
                {{ $t("Reviews") }})
              </span>
            </div>
            <!-- Share -->
            <div class="d-flex flex-wrap">
              <img
                src="@/assets/img/share/icons8-facebook-500.png"
                alt="facebook"
                class="cursor-pointer me-2"
                style="width: 40px; height: 40px"
                @click="share('facebook', game)"
              />
              <img
                src="@/assets/img/share/icons8-twitterx-500.png"
                alt="twitter"
                class="cursor-pointer me-2"
                style="width: 40px; height: 40px"
                @click="share('x', game)"
              />
              <img
                src="@/assets/img/share/icons8-instagram.svg"
                alt="instagram"
                class="cursor-pointer me-2"
                style="width: 48px; height: 40px"
                @click="share('instagram', game)"
              />
              <img
                src="@/assets/img/share/icons8-linkedin-500.png"
                alt="linkedin"
                class="cursor-pointer me-2"
                style="width: 40px; height: 40px"
                @click="share('linkedin', game)"
              />
              <img
                src="@/assets/img/share/icons8-whatsapp-500.png"
                alt="whatsapp"
                class="cursor-pointer"
                style="width: 40px; height: 40px"
                @click="share('whatsapp', game)"
              />
            </div>
          </div>
          <div class="p-4 text-center" v-if="isLoggedIn">
            <StarRating
              @input="changeRating"
              starSize="42"
              :rating="userRate"
            />
            <p class="">{{ $t("Rate this game") }}</p>
          </div>

          <!-- Fullscreen Options -->
          <div class="d-none d-md-block mx-2">
            <div
              class="d-flex align-items-center rounded-circle bg-dark py-2 px-4 cursor-pointer"
              @click="toggleFullscreen"
            >
              <img src="@/assets/img/game/fullscreen.svg" alt="fullscreen" />
              <span class="text-white ms-2">{{ $t("Fullscreen") }}</span>
            </div>
          </div>
        </div>
      </section>

      <hr class="bg-secondary my-3" />

      <section>
        <div class="mx-2 my-3">
          <h4 class="my-2">{{ $t("Description") }}:</h4>
          <p class="text-md text-justify text-gray" v-html="game.description"></p>
        </div>
        <div class="mx-2 my-3">
          <h4 class="my-2">{{ $t("Instructions") }}:</h4>
          <p class="text-md text-justify text-gray" v-html="game.instructions"></p>
        </div>
      </section>

      <hr class="bg-secondary my-3" />
    </div>
    <div class="crime-section pb-60" v-if="similarGames.length > 0">
      <div class="container-fluid">
        <div class="section-wrapper">
          <h4 class="left-title">{{ $t("Similar Games") }}</h4>
        </div>
        <MostViews :items="similarGames" />
      </div>
    </div>
    <div class="crime-section pb-60">
      <div class="container-fluid">
        <div class="section-wrapper">
          <h4 class="left-title">{{ $t("New Games") }}</h4>
        </div>
        <NewGames :items="newGames" />
      </div>
    </div>
    <hr class="bg-secondary my-3" />

    <!-- Comments Section - unique to gamezone-iq -->
    <section v-if="isLoggedIn || comments.length != 0">
      <div class="mx-3">
        <div class="my-4">
          <div class="d-flex justify-content-between my-4 cursor-pointer">
            <div class="d-flex align-items-center gap-3">
              <div
                class="rounded-circle bg-primary d-flex align-items-center justify-content-center"
                style="width: 40px; height: 40px"
              >
                <img
                  src="http://gamesportal.enthalpytech.com/images/icon/image_6599416c4d5b2.svg"
                  alt="Comments"
                  class="img-fluid"
                />
              </div>
              <h1 class="fs-4 my-0">
                {{ $t("Comments") }}
              </h1>
            </div>
          </div>
          <!-- Add a form to send a comment -->
          <div class="my-2">
            <div class="d-flex flex-column gap-3" v-if="isLoggedIn">
              <div class="d-flex gap-3">
                <div class="w-100">
                  <div class="d-flex align-items-center gap-3">
                    <img
                      v-lazy="userInfo.photo"
                      class="rounded-circle"
                      style="max-width: 32px"
                      alt="Comments"
                    />
                    <h1 class="fs-6 mb-0">
                      {{ userInfo.name || $t("User") + " #" + userInfo.id }}
                    </h1>
                  </div>
                  <form
                    @submit.prevent="onPostComment"
                    class="d-flex flex-column gap-2 mt-2"
                  >
                    <textarea
                      class="form-control"
                      :placeholder="$t('Write a comment...')"
                      v-model="comment"
                      rows="4"
                    ></textarea>
                    <div class="d-flex justify-content-end">
                      <button type="submit" class="btn btn-primary">
                        {{ $t("Send") }}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
            <div
              class="d-flex flex-column gap-3 mt-2"
              v-for="(comment, index) in comments"
              :key="index"
            >
              <div class="d-flex align-items-center gap-3">
                <div class="rounded-circle">
                  <img
                    v-lazy="comment.sender.photo"
                    class="rounded-circle"
                    style="width: 32px; height: 32px"
                    alt="Comments"
                  />
                </div>
                <div class="w-100">
                  <div class="d-flex justify-content-between align-items-center">
                    <h3 class="fs-6 mb-0">
                      {{ comment.sender.name || "User #" + comment.sender.id }}
                    </h3>
                    <p class="text-gray fs-7 mb-0">
                      {{ comment.created_date }}
                    </p>
                  </div>
                  <p class="text-gray fs-7">
                    {{ comment.comment }}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import { useGamesStore } from "@/store/games";
import { mapState, mapActions } from "pinia";
import { useAuthStore } from "@/store/auth";
import helpers from "@/helpers";
import StarRating from "@/components/partials/StarRating.vue";
import MostViews from "@/components/partials/GameRowSlider.vue";
import NewGames from "@/components/partials/GameRoundedSlider.vue";
import { GameManager } from '@/game-manager';

export default {
  name: "GamePage",
  components: {
    StarRating,
    MostViews,
    NewGames,
  },
  data() {
    return {
      canPlay: false,
      showGame: false,
      loading: false,
      isCheckingAccess: true,
      gameManager: null,
      isPageVisible: true,
      loadTimeout: null,
      comment: "",
    };
  },
  computed: {
    ...mapState(useAuthStore, ["isLoggedIn", "userInfo"]),
    ...mapState(useGamesStore, [
      "game",
      "userRate",
      "newGames",
      "similarGames",
      "voteAction",
      "comments",
    ]),
    // Ensure game URL uses HTTPS to prevent mixed content issues
    secureGameUrl() {
      if (!this.game?.url) return '';
      // Upgrade HTTP to HTTPS to prevent mixed content blocking
      if (this.game.url.startsWith('http://')) {
        console.warn('Game URL using HTTP, upgrading to HTTPS:', this.game.url);
        return this.game.url.replace('http://', 'https://');
      }
      return this.game.url;
    },
  },
  methods: {
    ...mapActions(useGamesStore, [
      "fetchGame",
      "voteGame",
      "postComment",
      "rateGame",
    ]),
    ...mapActions(useAuthStore, [
      "openPaymentPage",
    ]),
    
    async changeRating(value) {
      await this.rateGame(this.game.id, value);
      helpers.showToast(
        this.$t("Your rating has been saved successfully"),
        this.$t("Success"),
        "success"
      );
    },
    
    // gamezone-iq custom subscription/payment handling
    openSubscription() {
      // Save current url to redirect back after subscription
      localStorage.setItem("redirectUrl", this.$route.fullPath);

      let config = helpers.getConfig();
      if(config.subscription_type == "card"){
        if(!this.isLoggedIn){
          this.openLogin();
        } else {
          this.openPaymentPage()
        }
      } else {
        window.location.href = config.landing_page_url;
      }
    },
    
    toggleFullscreen() {
      const iframe = this.$refs.gameIframe;
      if (!iframe) return;
      
      let isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
      
      if (isIOS) {
        // iOS-specific fullscreen logic
        this.handleIOSFullscreen(iframe);
      } else {
        // Non-iOS fullscreen logic
        if (document.fullscreenElement) {
          document.exitFullscreen();
        } else {
          iframe.requestFullscreen().catch(err => {
            console.log(`Error attempting to enable fullscreen: ${err.message}`);
          });
        }
      }
    },
    
    handleIOSFullscreen(iframe) {
      // Toggle the 'ios-fullscreen' class to resize the iframe
      if (!iframe.classList.contains("ios-fullscreen")) {
        iframe.classList.add("ios-fullscreen");
      } else {
        iframe.classList.remove("ios-fullscreen");
      }
    },
    
    onEscKey(event) {
      if (event.key === "Escape") {
        // Exit fullscreen mode if in fullscreen
        if (document.fullscreenElement) {
          document.exitFullscreen();
        }

        // If in iOS fullscreen mode, remove the class
        const iframe = this.$refs.gameIframe;
        if (iframe && iframe.classList.contains("ios-fullscreen")) {
          iframe.classList.remove("ios-fullscreen");
        }
      }
    },
    
    // Handle page visibility changes
    handleVisibilityChange() {
      this.isPageVisible = !document.hidden;
      
      if (this.gameManager) {
        if (this.isPageVisible) {
          this.gameManager.resumeGame();
        } else {
          this.gameManager.pauseGame();
        }
      }
    },
    
    onGameLoad() {
      console.log('Game loaded successfully');
      this.loading = false;
      
      // Clear the load timeout since game loaded successfully
      if (this.loadTimeout) {
        clearTimeout(this.loadTimeout);
        this.loadTimeout = null;
      }
      
      // Initialize game manager
      if (!this.gameManager) {
        this.gameManager = new GameManager();
      }
      this.gameManager.init(this.$refs.gameIframe);
      
      // Setup visibility management
      if (!this.isPageVisible) {
        this.gameManager.pauseGame();
      }
    },
    
    onGameError() {
      console.error('Game failed to load');
      this.loading = false;
      this.showGame = false;
      
      // Clear the load timeout
      if (this.loadTimeout) {
        clearTimeout(this.loadTimeout);
        this.loadTimeout = null;
      }
      
      helpers.showToast(
        this.$t("Failed to load the game. Please try again."),
        this.$t("Error"),
        "error"
      );
    },
    
    // Check access and show game
    checkAccess() {
      // Don't reset states if game is already loaded and playing
      if (this.canPlay && this.showGame && !this.loading) {
        return;
      }
      
      this.canPlay = false;
      this.showGame = false;
      
      if (!this.game?.url) {
        this.isCheckingAccess = true;
        return;
      }
      
      // Check if game doesn't require subscription (free game)
      if (this.game.is_subscription == 0) {
        this.canPlay = true;
        this.showGame = true;
        this.isCheckingAccess = false;
        return; // Free game, no need to check subscription status
      }
      
      // For subscription games, wait until userInfo is loaded
      // userInfo will be {} initially, then populated from localStorage/API
      if (this.isLoggedIn && Object.keys(this.userInfo).length === 0) {
        // Still loading user info, keep checking state
        this.isCheckingAccess = true;
        return;
      }
      
      // Done checking - now we can determine access
      this.isCheckingAccess = false;
      
      // Game requires subscription - check if user is subscribed
      if (this.isLoggedIn && this.game.url && this.userInfo?.is_subscribed) {
        this.canPlay = true;
        this.showGame = true;
      }
    },
    
    // Comment handling - unique to gamezone-iq
    async onVoteGame(action) {
      await this.voteGame(this.game.id, action);
    },
    
    async onPostComment() {
      if (!this.comment) {
        helpers.showToast(
          this.$t("Please write a comment before sending it"),
          this.$t("Error"),
          "error"
        );
        return;
      }
      const result = await this.postComment(this.game.id, this.comment);
      this.comment = "";
      if (result) {
        helpers.showToast(
          this.$t("Comment sent successfully"),
          this.$t("Success"),
          "success"
        );
      } else {
        helpers.showToast(
          this.$t("An error occurred while sending the comment"),
          this.$t("Error"),
          "error"
        );
      }
    },

    share(platform, game) {
      if (!game || !game.id) {
        console.error("Game data is undefined or missing required properties.");
        return;
      }

      const url = `${window.location.origin}/game/${this.$route.params.slug}`;
      const title = encodeURIComponent(game.title);

      switch (platform) {
        case "facebook":
          window.open(
            `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
            "_blank"
          );
          break;
        case "x":
          window.open(
            `https://twitter.com/intent/tweet?text=${title}&url=${encodeURIComponent(url)}`,
            "_blank"
          );
          break;
        case "instagram":
          helpers.showToast(
            this.$t("To share on Instagram, copy the game link and share it on your story or post."),
            this.$t("Success"),
            "success"
          );
          navigator.clipboard.writeText(url);
          break;
        case "whatsapp":
          window.open(
            `https://api.whatsapp.com/send?text=${title} - ${encodeURIComponent(url)}`,
            "_blank"
          );
          break;
        case "linkedin":
          window.open(
            `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`,
            "_blank"
          );
          break;
        default:
          console.error("Unsupported platform:", platform);
      }
    },
  },
  
  mounted() {
    document.addEventListener("keydown", this.onEscKey);
    document.addEventListener('visibilitychange', this.handleVisibilityChange);
    
    // Initialize page visibility
    this.isPageVisible = !document.hidden;
  },

  beforeUnmount() {
    document.removeEventListener("keydown", this.onEscKey);
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
    
    // Clear the load timeout
    if (this.loadTimeout) {
      clearTimeout(this.loadTimeout);
      this.loadTimeout = null;
    }
    
    // Clean up game manager
    if (this.gameManager) {
      this.gameManager.destroy();
      this.gameManager = null;
    }
  },
  
  async created() {
    // Fetch game data
    await this.fetchGame(this.$route.params.slug);
    
    // Check access
    this.checkAccess();
  },
  
  // Watch for changes
  watch: {
    game() {
      if (this.game?.url) {
        this.checkAccess();
      }
    },
    
    isLoggedIn() {
      this.checkAccess();
    },
    
    userInfo: {
      handler() {
        this.checkAccess();
      },
      deep: true
    }
  }
};
</script>

<style>
.ios-fullscreen {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  z-index: 9999;
}

.text-gray {
  color: #A4A4A4 !important;
}

.fs-7 {
  font-size: .875rem;
  line-height: 1.25rem;
}

.game-iframe {
  height: 70vh;
  aspect-ratio: 16/9;
  border: none;
}

.subscription-message {
  background-color: rgba(15, 64, 134, 0.733);
  border-radius: 12px;
  margin: 0 32px;
  cursor: pointer;
}

.game-wrapper {
  position: relative;
  width: 100%;
  height: 70vh;
}

.loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(38, 38, 83, 0.9);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: white;
}

.loading-overlay .spinner-border {
  width: 3rem;
  height: 3rem;
  margin-bottom: 1rem;
}
</style>
